import {Kinetic} from './kinetic';
import {EasingZoomer} from './easing.zoomer';
import {Zoomer} from './zoomer';
import {EasingType} from './easing';

import {BrowserEventType, ViewerEventType} from './viewer.event.type';
import {EventHandler, ViewerEvent} from './viewer.event';
import {EventBuilder} from './event.builder';


import {Box2, PerspectiveCamera, Vector2, Vector3, WebGLRenderer} from 'three';

/** which cursor direction is implied by the key up/down? */
export enum PanZoomCursorDirection {
  UP,
  DOWN,
  LEFT,
  RIGHT,
  IN,
  OUT,
  NONE
}

/** the states the panzoom pseudo camera can be in */
export enum PanZoomState {
  STEADY     = 'steady',
  PRESSED    = 'pressed',
  DRAGGING   = 'dragging',
  SCROLLING  = 'scrolling'
}

/** options for the PanZoom class */
export class PanZoomOptions {

  /** the closest we can zoom in */
  public maxZoom: number         = 1;

  /** the furthest we can zoom out */
  public minZoom: number         = 10000;

  /** the step size for zooming */
  public zoomStep: number        = 0.06;

  /** the delta to be used for zooming without a mouse wheel */
  public zoomInterval: number    = 100;

  /** control for exponential decay; 325 gives you ~2 seconds of animation */
  public timeConstant: number    = 325;

  /** threshold for doing momentum auto-scrolling */
  public minimumVelocity: number = 5;

  /** how big to make the momentum effect */
  public amplitude: number       = 0.25;

  /** the 3D world bound of the scene we are panning/zooming */
  public bound: Box2 = new Box2();

  /** standard constructor */
  constructor(init?: Partial<PanZoomOptions>) {

    Object.assign(this, init);
  }
}

/** helper to track the reference point at which the user clicked the mouse */
class Click {

  /** the original event */
  public event: BrowserEventType           = null;

  /** was it a left or right button click */
  public isRightClick: boolean             = false;

  /** the horizontal position on the canvas */
  public x: number                         = 0;

  /** the vertical position on the canvas */
  public y: number                         = 0;

  /** the time of the click */
  public timestamp: number                 = 0;

  /** standard constructor */
  constructor(init?: Partial<Click>) {

    Object.assign(this, init);
  }
}

/**
 *
 * @class PanZoom
 *
 * @brief a pseudo camera to replace the standard Three.js camera.  Our camera is a little more
 * flexible and does panning and zooming the way we want.
 *
 * For kinetic panning, we use the ideas from:
 *
 *   https://ariya.io/tags/kinetic
 *   https://www.youtube.com/watch?v=JU5ImU1QHU4
 *   https://github.com/ariya/kinetic/blob/master/3/scroll.js
 *   https://github.com/anvaka/panzoom/
 *
 * This approach lets us create a physical momentum to the panning.  We don't use it for zooming,
 * since the mouse wheel interaction is generally fine-grained, and linear is fine.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
export class PanZoom {

  /** the easing mode for zooming along the z-axis */
  private easingMode: EasingType = EasingType.LINEAR;

  /** the actual renderer */
  private renderer: WebGLRenderer|null          = null;

  /** the actual camera attached to the actual renderer */
  // @ts-ignore
  private camera: PerspectiveCamera;

  /** the z-value that would normally let us see teh auto-fitted graph */
  private fullZ: number = 1000;

  /** the current state of the pseudo camera */
  private state: PanZoomState                   = PanZoomState.STEADY;

  /** the most recent click */
  private lastClick: Click                      = new Click();

  /** configuration options */
  private options: PanZoomOptions               = new PanZoomOptions();

  /** the current screen position we are panned to. */
  private position: {x: number, y: number}      = {x: 0, y: 0};

  /** momentum based scroller, for when we are auto-scrolling */
  private smoothScroll: Kinetic                 = new Kinetic(
    (): {x: number, y: number} => { return {x: 0, y: 0}},
    (x: number, y: number): void => {},
    (): void => {});

  /** the registered event handlers */
  private handlers: Map<string, EventHandler[]> = new Map<string, EventHandler[]>();

  /** our event factory */
  private eventBuilder: EventBuilder            = new EventBuilder();

  /** the optimal camera zoom, will just encompass the entire graph. */
  private perfectZ: number                      = 0;

  /** the adapter we use for different models of zooming in/out */
  public zoomer: Zoomer|null                    = null;

  /** standard constructor */
  constructor(renderer: WebGLRenderer,
              camera:   PerspectiveCamera,
              options?: PanZoomOptions) {

    let pz: PanZoom = this;

    pz.renderer = renderer;
    pz.camera   = camera;

    /* did they give any options? */

    if (options) {

      Object.assign(pz.options, options);
    }

    /* set up the kinetic scroller ... */

    pz.smoothScroll = new Kinetic(
      (): {x: number, y: number} => { return pz.position },
      (x: number, y: number): void => { pz.moveTo(x, y) },
      (): void => {pz.onScrollEnd()},
      pz.options.minimumVelocity,
      pz.options.amplitude,
      pz.options.timeConstant
    );

    /* take the input focus if possible */

    pz.renderer.domElement.focus();

    /* listen for all the events! */

    pz.bindEvents();

    /* set the extent of what the camera should be seeing */

    pz.setBound(pz.options.bound);

    pz.createZoomer();

    pz.renderer.domElement.addEventListener('wheel', (event: WheelEvent): void => {

      pz.zoomer.scrolled(event);
    });
  }

  /** helper to (re)create zoomer */
  private createZoomer(): void {

    let pz: PanZoom = this;

    /*
    pz.zoomer = new MagicZoomer(
    pz.renderer.domElement,
      ()   => pz.camera.position.z,
      () => pz.camera.near,
      ()  => pz.camera.far,
      (delta: number, event: WheelEvent): void => {pz.onDeltaWheel(delta, event);},
      (event: WheelEvent): boolean => {return pz.filtered(event);}
    );
    */

    let oldEasing: EasingType = pz.easingMode;

    if (pz.zoomer) {

      oldEasing = pz.zoomer.getEasing();
    }

    pz.zoomer = new EasingZoomer(
      pz.renderer.domElement,
      ()   => pz.camera.position.z,
      () => pz.camera.near,
      ()  => pz.fullZ,
      (delta: number, event: WheelEvent): void => {pz.onDeltaWheel(delta, event);},
      (event: WheelEvent): boolean => {return pz.filtered(event);}
    );

    pz.zoomer.setEasing(oldEasing);

  }

  /**
   * configure the z value that lets us see the full graph, or at least the full
   * distance scale (Y-axis).
   *
   * @param z
   */
  public setFullZoomOut(z: number): void {

    let pz: PanZoom = this;

    pz.fullZ = z;

    /* we changed the extent of the zoom range, have to recreate teh zoomer */

    pz.createZoomer();

    pz.zoomer.update();
  }

  /** force the viewing bound for the camera */
  public setBound(bound: Box2): void {

    let pz: PanZoom = this;

    pz.options.bound = bound;

    /*
     * for the scene we are doing our panning and zooming on, what is the outer
     * bound?  we can use this to automatically determine what our zoom factors
     * should be.
     *
     */

    pz.perfectZ = pz.options.maxZoom;

    if (pz.options.bound.getSize(new Vector2()).x == 0 || pz.options.bound.getSize(new Vector2()).y == 0) {

      /* no bounding box yet */

      return;
    }

    pz.perfectZ             = pz.zForBox(pz.options.bound);

    pz.options.minZoom      = pz.perfectZ * 3;
    pz.options.maxZoom      = 0.0001;
    pz.options.zoomInterval = pz.perfectZ / 4;

    /* make sure actual camera is up-to-date */

    pz.updateProjection();

    pz.zoomer?.update();

    //Logger.debug(`[PanZoom] zoom options perfect=${pz.perfectZ}, min=${pz.options.minZoom}, max=${pz.options.maxZoom}, step=${pz.options.zoomStep}, incr=${pz.options.zoomInterval},`)
  }

  /** fetch the configuration options */
  public getOptions(): PanZoomOptions {

    return this.options;
  }

  /**
   * add our event bindings, in capture mode, don't let anything bubble up...we will
   * generate our own event stream for the application to use.
   *
   */
  private bindEvents(): void {

    let pz: PanZoom = this;

    /* capture keyboard events */

    pz.renderer.domElement.onkeyup = (event: BrowserEventType) => {

      /* emit the raw keyboard even for client application as well */

      pz.emit(
        pz.eventBuilder
          .setOriginalEvent(event)
          .setType(ViewerEventType.KEY_UP)
          .build());

      /* do our pan/zoom actions */

      pz.onKeyUp(event);
    };

    pz.renderer.domElement.onkeydown = (event: BrowserEventType) => {

      /* emit the raw keyboard even for client application as well */

      pz.emit(
        pz.eventBuilder
          .setOriginalEvent(event)
          .setType(ViewerEventType.KEY_DOWN)
          .build());

      /* do our pan/zoom actions */

      pz.onKeyDown(event);

    };

    /* capture the mouse wheel events on the canvas */

    pz.renderer.domElement.onwheel = (event: BrowserEventType) => {

      /* take the focus so keyboard events will start */

      pz.renderer.domElement.focus();

      /* prevent bubble up */

      event?.preventDefault();

      /* pass through a raw wheel event as well */

      let x: number = pz.eventX(event);
      let y: number = pz.eventY(event);

      let {wx, wy} = pz.screenToWorld(x,y);

      pz.emit(
        pz.eventBuilder
          .setOriginalEvent(event)
          .setType(ViewerEventType.MOUSE_WHEEL)
          .setData({
            sx: x,
            sy: y,
            wx,
            wy
          })
          .build()
      );

      /* zoom! ... but only if there is no modifier (SHIFT or CTRL) */

      /*
       * This is now handled by the Zoomer
       *
      if (!pz.eventShift(event) && !pz.eventCtrl(event)) {

        pz.onWheel(event);
      }
       *
       */
    };

    /*
     * capture the mouse click/release and mouse movement; we follow mouse
     * movement at the window level, in case they drag the mouse outside
     * the canvas.
     *
     */

    if (typeof window.ontouchstart !== 'undefined') {

      pz.renderer.domElement.addEventListener(
        'touchstart',
        (event: BrowserEventType): void => {

          /* take the focus so keyboard events will start */

          pz.renderer.domElement.focus();

          pz.tap(event)
        }
      );

      window.addEventListener(
        'touchmove',
        (event: BrowserEventType): void => {

          /* take the focus so keyboard events will start */

          pz.renderer.domElement.focus();

          pz.drag(event);
        }
      );

      window.addEventListener(
        'touchend',
        (event: BrowserEventType): void => {

          pz.release(event);
        }
      );
    }

    pz.renderer.domElement.addEventListener(
      'mousedown',
       (event: BrowserEventType): void => {

         /* take the focus so keyboard events will start */

         pz.renderer.domElement.focus();

         pz.tap(event);
      }
    );

    window.addEventListener(
      'mousemove',
      (event: BrowserEventType): void => {

        /*
         * don't take the focus on mouse movement, if the GUI has something like a dropdown
         * being shown, on top of our component, then we will be disrupting that component
         * (causing a blur event), and it won't be obvious why it happened; all they see is
         * the focus being taken away.
         *
        pz.renderer.domElement.focus();
         *
         */

        pz.drag(event);
      }
    );

    window.addEventListener(
      'mouseup',
      (event: BrowserEventType): void => {

        pz.release(event);


      }
    );

    pz.renderer.domElement.addEventListener(
      'click',
      (event: BrowserEventType): void => {

        /* take the focus so keyboard events will start */

        pz.renderer.domElement.focus();

        /* emit mouse double click event */

        let x: number = pz.eventX(event);
        let y: number = pz.eventY(event);

        let {wx, wy} = pz.screenToWorld(x,y);

        pz.emit(
          pz.eventBuilder
            .setOriginalEvent(event)
            .setType(ViewerEventType.SINGLE_CLICK)
            .setData({
              sx: x,
              sy: y,
              wx,
              wy
            })
            .build()
        );

      });

    pz.renderer.domElement.oncontextmenu = (event: BrowserEventType): void => {

      pz.rightClick(event);
    };

    /* track double clicks as well https://developer.mozilla.org/en-US/docs/Web/API/Element/dblclick_event */

    pz.renderer.domElement.addEventListener(
      'dblclick',
      (event: BrowserEventType): void => {

        /* take the focus so keyboard events will start */

        pz.renderer.domElement.focus();

        /* emit mouse double click event */

        let x: number = pz.eventX(event);
        let y: number = pz.eventY(event);

        let {wx, wy} = pz.screenToWorld(x,y);

        pz.emit(
          pz.eventBuilder
            .setOriginalEvent(event)
            .setType(ViewerEventType.DOUBLE_CLICK)
            .setData({
              sx: x,
              sy: y,
              wx,
              wy
            })
            .build()
        );
      }
    );

  }

  /** fetch the X position of the event */
  private eventX(event: BrowserEventType): number {

    let pz: PanZoom = this;

    if (!pz.renderer) {

      return 0;
    }

    let rect = pz.renderer.domElement.getBoundingClientRect();

    /* was it a touch? */

    if ((event as TouchEvent).targetTouches && ((event as TouchEvent).targetTouches.length >= 1)) {

      return (event as TouchEvent).targetTouches[0].clientX - rect.left;
    }

    /* it was the mouse */

    return (event as MouseEvent).clientX - rect.left;
  }

  /** fetch the Y position of the event */
  private eventY(event: BrowserEventType): number {

    let pz: PanZoom = this;

    if (!pz.renderer) {

      return 0;
    }

    let rect = pz.renderer.domElement.getBoundingClientRect();


    /* was it a touch? */

    if ((event as TouchEvent).targetTouches && ((event as TouchEvent).targetTouches.length >= 1)) {

      return (event as TouchEvent).targetTouches[0].clientY - rect.top;
    }

    /* it was the mouse */

    return (event as MouseEvent).clientY - rect.top;
  }

  /** check if the SHIFT key is on during this event */
  private eventShift(event: BrowserEventType): boolean {

    if (!event) {

      return false;
    }

    return 'shiftKey' in event && event.shiftKey;
  }

  /** check if the CTRL key is on during this event */
  private eventCtrl(event: BrowserEventType): boolean {

    if (!event) {

      return false;
    }

    return 'ctrlKey' in event && event.ctrlKey;
  }

  /** generate a context menu pop event */
  private rightClick(event: BrowserEventType): void {

    //Logger.debug(`[PanZoom] rightClick - context menu init:`, event);

    let pz: PanZoom = this;

    /*
     * we are currently in the middle of an auto-scroll...shut that down,
     * we want to back in STEADY state and ready to pop the context menu.
     *
     */

    if (pz.state == PanZoomState.SCROLLING) {

      /* force back to STEADY */

      pz.state    = PanZoomState.PRESSED;

      pz.smoothScroll.cancel(true);
    }

    /* prevent bubble up, we will emit our own event for the application to use */

    /*
    event?.preventDefault();
    event?.stopPropagation();
    */

    let x: number = pz.eventX(event);
    let y: number = pz.eventY(event);

    let {wx, wy} = pz.screenToWorld(x,y);

    /* emit context menu event */

    pz.emit(
      pz.eventBuilder
        .setOriginalEvent(event)
        .setType(ViewerEventType.CONTEXT_MENU)
        .setData({
          sx: x,
          sy: y,
          wx,
          wy
        })
        .build()
    );
  }

  /** handle key press */
  private onKeyUp(event: BrowserEventType): void {

    let pz: PanZoom = this;


  }

  /** decode a keyboard event into a cursor command */
  private cursorDirection(event: BrowserEventType): PanZoomCursorDirection {

    let keyMap: any = {
      Equal:      PanZoomCursorDirection.IN,
      Minus:      PanZoomCursorDirection.OUT,
      ArrowUp:    PanZoomCursorDirection.UP,
      ArrowDown:  PanZoomCursorDirection.DOWN,
      ArrowLeft:  PanZoomCursorDirection.LEFT,
      ArrowRight: PanZoomCursorDirection.RIGHT,

    }

    let code: string = (event as KeyboardEvent).code;

    if (code in keyMap) {

      return keyMap[code];
    }

    /* it's not a basic movement key */

    return PanZoomCursorDirection.NONE;
  }

  /** handle key release */
  private onKeyDown(event: BrowserEventType): void {

    let pz: PanZoom = this;

    /* prevent bubble up */

    event?.preventDefault();
    event?.stopPropagation();

    /* - - - - - - - - - - - modifiers? - - - - - - - - - - - - - */

    let shiftKey: boolean = false;
    let ctrlKey: boolean  = false;
    let altKey: boolean   = false;

    if ((event as KeyboardEvent).shiftKey) {

      shiftKey = true;
    }

    if ((event as KeyboardEvent).ctrlKey) {

      ctrlKey = true;
    }

    if ((event as KeyboardEvent).altKey) {

      altKey = true;
    }

    /* - - - - - - - - - - - easing mode - - - - - - - - - - - - */

    if ((event as KeyboardEvent).code == 'BracketLeft') {

      /* cycle backward in the easing modes */

      pz.zoomer.previousEasingMode();

      return ;

    } else if ((event as KeyboardEvent).code == 'BracketRight') {

      /* cycle forward in the easing modes */

      pz.zoomer.nextEasingMode();

      return ;

    } else if  ((event as KeyboardEvent).code == 'Backslash') {

      /* reset easing to linear mode */

      pz.zoomer.resetEasing();

      return ;
    }

    /* - - - - - - - - - - - pan / zoom - - - - - - - - - - - - - */

    let direction: PanZoomCursorDirection = pz.cursorDirection(event);

    if (direction == PanZoomCursorDirection.NONE) {

      /* nothing to do, right now we only handle basic movement */

      return ;
    }

    if (direction == PanZoomCursorDirection.IN || direction == PanZoomCursorDirection.OUT) {

      if (direction == PanZoomCursorDirection.IN) {

        pz.zoomStep(false);

        return ;

      }

      pz.zoomStep(true);

      return ;
    }

    /*
     * we're panning, we make the pan speed be proportional to the canvas (view port) size
     * we also allow fine grain control; use SHIFT plus the cursor keys to move in smaller
     * increments.
     *
     */

    let dx: number          = 1;
    let dy: number          = 1;
    let speedRatio: number  = shiftKey ? 0.01: 0.10;
    let bound: DOMRect      = pz.renderer.domElement.getBoundingClientRect();
    let offset: number      = Math.min(bound.width, bound.height);

    if (direction == PanZoomCursorDirection.LEFT || direction == PanZoomCursorDirection.RIGHT) {

      dy = 0;

      if (direction == PanZoomCursorDirection.LEFT) {

        dx = -1;

      }
    }

    if (direction == PanZoomCursorDirection.UP || direction == PanZoomCursorDirection.DOWN ) {

      dx = 0;

      if (direction == PanZoomCursorDirection.UP) {

        dy = -1;
      }
    }

    dx = offset * speedRatio * dx;
    dy = offset * speedRatio * dy;

    pz.moveBy(dx, dy);

  }

  /** zoom in/out center of window by steps (false means zoom in) */
  private zoomStep(direction: boolean = true): void {

    let pz: PanZoom = this;

    /* cancel any existing zoom animation, we are doing a manual step. */

    pz.zoomer?.cancel();

    let deltaZ: number = pz.options.zoomInterval * pz.options.zoomStep * (1/pz.options.zoomStep);

    /* we want to zoom in the center of the camera */

    let bound: Box2                      = pz.worldViewBound();
    const c: Vector2                     = bound.getCenter(new Vector2());
    let {sx: cx, sy: cy} = pz.worldToScreen(c.x, c.y);

    if (!direction) {

      deltaZ *= -1;
    }

    //Logger.debug(`[PanZoom] centered zoom: x=${cx}, y${cy}, deltaZ=${deltaZ}`);

    this.zoomOnPoint(cx, cy, deltaZ);
  }

  /** simplified zooming out */
  public zoomOut(): void {

    this.zoomStep();
  }

  /** simplified zooming in */
  public zoomIn(): void {

    this.zoomStep(false);
  }

  /** handle mouse press */
  private tap(event: BrowserEventType): void {

    let pz: PanZoom = this;

    /*
    {
      Logger.debug("X: geom: ", {width: pz.renderer.domElement.width, height:pz.renderer.domElement.height});
      Logger.debug("X: world center: ", {wx: pz.camera.position.x, wy: pz.camera.position.y});
      Logger.debug("X: world => screen center: ", pz.worldToScreen(pz.camera.position.x, pz.camera.position.y));
      Logger.debug("X: click: ", {x: pz.eventX(event), y:pz.eventY(event)});
      let wp = pz.screenToWorld(pz.eventX(event), pz.eventY(event));
      Logger.debug("X: click => world: ", wp);
      let sp = pz.worldToScreen( wp.wx, wp.wy);
      Logger.debug("X: click => world => screen: ", sp);

    }
    */


    /*
     * NOTE: we have to allow tap to cancel an existing auto-scroll,
     * otherwise if you click while its scrolling (which be up to ~2sec),
     * nothing will happen...which is more often awkward than not.  sometimes,
     * you will actually want to "grab it" and stop it.
     *
     * We also check BEFORE let or right click check; if they bring up
     * the context menu, we should stop scrolling as wel.
     *
     */

    if (pz.state == PanZoomState.SCROLLING) {

      /* force back to STEADY */

      //Logger.debug(`[PanZoom] tap - CANCELING auto-scroll...`);

      pz.state = PanZoomState.STEADY;

      pz.smoothScroll.cancel(true);
    }

    /* we should only be able to tap from the resting state */

    if (pz.state != PanZoomState.STEADY) {

      /* if the browser gets trigger-happy, just ignore */

      event?.stopPropagation();

      return;
    }

    let isLeftButton: boolean = true;

    if (!(event as TouchEvent).targetTouches || !((event as TouchEvent).targetTouches.length >= 1)) {

      /* touches can only be left clicks, for mouse we have to check the button code */

      /*
       * if it wasn't a touch, then we just need to figure out if something other than the
       * left button was clicked, but note also that the modern way to get the context action,
       * is just watch for the oncontextmenu event, because there is keyboard binding for that
       * in the browser.
       *
       *   ref: https://stackoverflow.com/questions/2405771/is-right-click-a-javascript-event
       */

      if ("which" in (event as MouseEvent)) {

        // Gecko (Firefox), WebKit (Safari/Chrome) & Opera

        isLeftButton = (event as MouseEvent).which != 3;

      } else if ("button" in (event as MouseEvent)) {

        /* IE, Opera */

        isLeftButton = (event as MouseEvent).button != 2;
      }
    }

    if (!isLeftButton) {

      /* we generally reserve right-clicking for our context menu behavior */

      pz.rightClick(event);

      return;
    }

    pz.lastClick = new Click({
      event,
      isRightClick: !isLeftButton,
      x: pz.eventX(event),
      y: pz.eventY(event),
      timestamp: performance.now()
    });

    /* prevent bubble up */

    event?.preventDefault();
    event?.stopPropagation();

    //Logger.debug(`[PanZoom] tap - entering PRESSED state.`);

    /* move to pressed state */

    pz.state = PanZoomState.PRESSED;

    /* take note of our starting position */

    pz.position = {x: pz.lastClick.x, y: pz.lastClick.y};

    /* cancel any existing momentum scroll */

    pz.smoothScroll.cancel();

    /*
     * when the mouse starts moving...we will use Kinetic to track the velocity...
     * so that later in auto-scrolling mode, we know how much momentum to give
     * the auto-scrolling. When they release the mouse button, we can move into
     * auto-scrolling mode.
     *
     * while the button is pressed, and they manually drag...we just do manual
     * panning.
     *
     */

    //Logger.debug(`[PanZoom] tap - x=${pz.position.x}, y=${pz.position.y}`);

    /* emit mouse down event */

    let x: number = pz.eventX(event);
    let y: number = pz.eventY(event);

    let {wx, wy} = pz.screenToWorld(x,y);

    pz.emit(
      pz.eventBuilder
        .setOriginalEvent(event)
        .setType(ViewerEventType.MOUSE_DOWN)
        .setData({
          sx: x,
          sy: y,
          wx,
          wy
        })
        .build()
    );

  }

  /** handle mouse release */
  private release(event: BrowserEventType): void {

    let pz: PanZoom = this;

    pz.renderer.domElement.style.cursor = 'default';

    /* you can only release if you pressed, or you pressed and are dragging */

    if ((pz.state != PanZoomState.PRESSED) && (pz.state != PanZoomState.DRAGGING))  {

      //Logger.debug(`[PanZoom] release - ignoring.`);

      /* if the browser gets trigger-happy, just ignore */

      event?.stopPropagation();

      return ;
    }

    /* prevent bubble up */

    event?.preventDefault();
    event?.stopPropagation();

    /* emit mouse up event */

    let x: number = pz.eventX(event);
    let y: number = pz.eventY(event);

    let {wx, wy} = pz.screenToWorld(x,y);

    pz.emit(
      pz.eventBuilder
        .setOriginalEvent(event)
        .setType(ViewerEventType.MOUSE_UP)
        .setData({
          sx: x,
          sy: y,
          wx,
          wy
        })
        .build()
    );

    /*
     * tell Kinetic we are done tracking the manual drag, and now it's time to
     * start auto-scrolling
     *
     */

    if (pz.state == PanZoomState.DRAGGING) {

      pz.state = PanZoomState.SCROLLING;

      //Logger.debug(`[PanZoom] release - entering SCROLLING state.`);

      pz.smoothScroll.stop();

      /*
       * kinetic will call our onScrollEnd() method when the auto-scrolling
       * finishes (unless we cancel it before then).
       *
       */

      return ;
    }

    /* if they just press and release, return to steady state */

    pz.state = PanZoomState.STEADY;

    //Logger.debug(`[PanZoom] release - entering STEADY state.`);

  }

  /** handle mouse release */
  private drag(event: BrowserEventType): void {

    let pz: PanZoom = this;

    /*
     * dragging can continue dragging, or be started while you have the mouse
     * button pressed.
     *
     */
    if ((pz.state != PanZoomState.DRAGGING) && (pz.state != PanZoomState.PRESSED)) {

      return;
    }

    pz.renderer.domElement.style.cursor = 'grab';

    if (pz.state != PanZoomState.DRAGGING) {

      //Logger.debug(`[PanZoom] drag - entering DRAGGING state.`);

      /*
       * start tracking the drag velocity, we must call stop() when the mouse button is released
       * that is the trigger to start the momentum based auto-scrolling
       *
       */

      pz.smoothScroll.start();

      /* emit the drag/pan start event */

      let x: number = pz.position.x;
      let y: number = pz.position.y;

      let {wx, wy} = pz.screenToWorld(x,y);

      pz.emit(
        pz.eventBuilder
          .setOriginalEvent(event)
          .setType(ViewerEventType.PAN_START)
          .setData({
            sx: x,
            sy: y,
            wx,
            wy
          })
          .build()
      );

      pz.emit(
        pz.eventBuilder
          .setOriginalEvent(event)
          .setType(ViewerEventType.DRAG_START)
          .setData({
            sx: x,
            sy: y,
            wx,
            wy
          })
          .build()
      );
    }

    /* definitely dragging now! */

    pz.state = PanZoomState.DRAGGING;

    /* how far have we dragged to? */

    let dest: {x: number, y: number} = {x: pz.eventX(event), y: pz.eventY(event)};
    let dx: number                   = dest.x - pz.position.x;
    let dy: number                   = dest.y - pz.position.y;

    /* manually pan */

    pz.moveBy(dx, dy);

    /* update current position */

    pz.position = dest;

    /* emit mouse drag event */

    let x: number = pz.eventX(event);
    let y: number = pz.eventY(event);

    let {wx, wy} = pz.screenToWorld(x,y);

    pz.emit(
      pz.eventBuilder
        .setOriginalEvent(event)
        .setType(ViewerEventType.DRAG)
        .setData({
          sx: x,
          sy: y,
          wx,
          wy,
          dx,
          dy
        })
        .build()
    );

  }

  /** test if we are ignoring this mouse wheel event */
  private filtered(event: WheelEvent): boolean {

    let pz: PanZoom = this;

    return !(!pz.eventShift(event) && !pz.eventCtrl(event));
  }

  /** zoom in or out by the given delta */
  private onDeltaWheel(delta: number, event: WheelEvent) {

    let pz: PanZoom = this;

    //Logger.debug(`[PanZoom] onDeltaWheel - delta=${delta}, event: `, event);

    pz.zoomOnPoint(pz.eventX(event), pz.eventY(event), delta, true);
  }

  /** handle mouse wheel events */
  private onWheel(event: BrowserEventType): void {

    let pz: PanZoom = this;

    const { clientX, clientY, deltaY } = event as WheelEvent;

    //Logger.debug(`[PanZoom] onWheel - event: `, event);

    pz.zoomOnPoint(pz.eventX(event), pz.eventY(event), deltaY);

  }

  /** invoked by the kinetic scroller when it's done scrolling */
  public onScrollEnd(): void {

    let pz: PanZoom = this;

    /* return to steady state */

    pz.state = PanZoomState.STEADY;

    //Logger.debug(`[PanZoom] onScrollEnd - entering STEADY state.`);

    /* emit the drag/pan end event */

    let x: number = pz.position.x;
    let y: number = pz.position.y;

    let {wx, wy} = pz.screenToWorld(x,y);

    pz.emit(
      pz.eventBuilder
        .setType(ViewerEventType.PAN_END)
        .setData({
          sx: x,
          sy: y,
          wx,
          wy
        })
        .build()
    );

    pz.emit(
      pz.eventBuilder
        .setType(ViewerEventType.DRAG_END)
        .setData({
          sx: x,
          sy: y,
          wx,
          wy
        })
        .build()
    );

  }

  /** move to the 3D world position implied by this mouse/screen position */
  public moveTo(sx: number, sy: number): void {

    let pz: PanZoom = this;

    //Logger.debug(`[PanZoom] moveTo - [src] x=${pz.position.x}, y=${pz.position.y}, [dest] x=${sx}, y=${sy}`);

    /* what is the 3D world position to move to? */

    let {wx : futureX, wy: futureY}   = pz.screenToWorld(sx, sy);
    let {wx : currentX, wy: currentY} = pz.screenToWorld(pz.position.x, pz.position.y);

    let dx: number = currentX - futureX;
    let dy: number = currentY - futureY;

    /* actually move the camera ... */

    //Logger.debug(`[PanZoom] moveTo - [delta] dx=${dx}, dy=${dy}, [src] x=${currentX}, y=${currentY}, [dest] x=${futureX}, y=${futureY}`);

    pz.camera.position.set(
      pz.camera.position.x + dx,
      pz.camera.position.y + dy,
      pz.camera.position.z
    );

    pz.position = {x: sx, y: sy};

    pz.updateProjection();

    pz.emit(
      pz.eventBuilder
        .setType(ViewerEventType.PAN)
        .setData({
          sx,
          sy,
          wx: futureX,
          wy: futureY
        })
        .build()
    );

  }

  public moveBy(dx: number, dy: number) {

    let pz: PanZoom = this;

    //Logger.debug(`[PanZoom] moveBy - x=${dx}, y=${dy}`);

    pz.moveTo(pz.position.x + dx, pz.position.y + dy);
  }

  /**
   *  convert the mouse position into a relative percentage position and
   *  ensure that the co-ordinate system is from -1,1 upper left to 1,-1
   *  in the lower right, that is, the y-axis is the way we expect;
   *  increasing upwards.
   *
   *    -1 == 0 and 1 == width/height of canvas
   *
   * This co-ordinate space is also the same one used by the actual camera,
   * to define its viewing frustum.
   *
   * @param sx the mouse horizontal position
   * @param sy the mouse vertical position
   *
   * @return
   */
  private screenToPercent(sx: number, sy: number): {px: number, py: number} {

    /*
     * ref: https://medium.com/@ceccarellisimone1/zoom-and-pan-in-three-js-customly-simple-interactions-in-data-graph-visualization-26643a5d0287
     * ref: https://stackoverflow.com/questions/13055214/mouse-canvas-x-y-to-three-js-world-x-y-z
     */

    let pz: PanZoom                 = this;
    const canvas: HTMLCanvasElement = pz.renderer.domElement;

    return {
      px:  (sx / canvas.clientWidth)  * 2 - 1,
      py: -(sy / canvas.clientHeight) * 2 + 1
    };
  }

  /**
   * fetch the bounding box of what the camera can currently see. We allow a
   * different height to be specified (trialZ), so that "what if" games can
   * be played, what would the camera's extent be at trialZ?  If no custom
   * height is provided, then we just use the camera's current height.
   *
   * @param trialZ (optional) the height of the camera where we want to know
   * its viewing extent. Essentially a "what if" zoom level.
   *
   * @private
   */
  public worldViewBound(trialZ?: number): Box2 {

    /*
     *  ref: https://medium.com/@ceccarellisimone1/zoom-and-pan-in-three-js-customly-simple-interactions-in-data-graph-visualization-26643a5d0287
     *  ref: https://stackoverflow.com/questions/14614252/how-to-fit-camera-to-object
     *  ref: https://discourse.threejs.org/t/camera-zoom-to-fit-object/936/23
     *
     */

    let pz: PanZoom = this;

    if (!trialZ) {

      trialZ = pz.camera.position.z
    }

    const fov: number        = this.camera.fov;
    const aspect: number     = this.camera.aspect;
    const halfHeight: number = Math.tan((fov * Math.PI) / 180 / 2) * trialZ;
    const halfWidth: number  = halfHeight * aspect;

    /*
     * return the bounding box of what the camera can see, remember,
     * we are just drawing a graph of lines on the x/y plane, there is
     * no depth.
     *
     */

    return new Box2(
      new Vector2(
        this.camera.position.x - halfWidth,
        this.camera.position.y - halfHeight),
      new Vector2(
        this.camera.position.x + halfWidth,
        this.camera.position.y + halfHeight
      ));
  }

  /**
   * convert a 3D world x/y position into a mouse cursor position on the canvas.  Co-ordinates will
   * be relative to the canvas, NOT the screen or main browser window.  This is the opposite call
   * for screenToWorld().
   *
   * @param wx the 3D world horizontal position
   * @param wy the 3D world vertical position
   *
   */
  public worldToScreen(wx: number, wy: number): {sx: number, sy: number} {

    /*
     * ref: https://discourse.threejs.org/t/how-to-converting-world-coordinates-to-2d-mouse-coordinates-in-threejs/2251/2
     * ref: https://stackoverflow.com/questions/11586527/converting-world-coordinates-to-screen-coordinates-in-three-js-using-projection
     */

    let pz: PanZoom        = this;
    let widthHalf: number  = pz.renderer.domElement.getBoundingClientRect().width / 2;
    let heightHalf: number = pz.renderer.domElement.getBoundingClientRect().height / 2;

    let vec: Vector3       = new Vector3(wx, wy,0);
    vec.project(pz.camera);

    vec.x                  =   ( vec.x * widthHalf )  + widthHalf;
    vec.y                  = - ( vec.y * heightHalf ) + heightHalf;

    return {sx: Math.round(vec.x), sy: Math.round(vec.y)};
  }

  /**
   * convert a mouse position on the screen to its corresponding position in the 3D world.
   * At the same time, we allow for a custom or "trial" height of the camera (trialZ).  This
   * lets us play "what if games"; what would the 3D world position be, if the camera was
   * actually at the trialZ height? i.e. what would the projected point be at trial zoom
   * level?
   *
   * @param sx      the horizontal position of the mouse on the canvas
   * @param sy      the vertical position of the mouse on the canvas
   * @param trialZ  (optional) the camera height at which we want to do the projection
   * @param relative return co-ordinates relative to the camera
   *
   * from screen to the 3D world.  Essentially a "what if" zoom level.
   *
   * @private
   */
  public screenToWorld(sx: number, sy: number, trialZ?: number, relative: boolean=true): {wx: number, wy: number} {

    let pz: PanZoom                         = this;

    /* if they didn't specify a camera height, just use the current camera height */

    if (!trialZ) {

      trialZ = pz.camera.position.z;
    }

    /* what is our relative position on the screen? */

    let percent:  { px: number, py: number } = pz.screenToPercent(sx, sy);

    /* what is our current absolute position in the world? */

    let absolute: { x: number,  y: number }  = pz.camera.position;

    let viewBound: Box2 = pz.worldViewBound(trialZ);

    /*
     * now just give the 3D world position based on its relative (percentage)
     * position, anchored by where the camera is currently positions over
     * the x/y plane (where we draw the graph).
     *
     * the viewing frustum of the camera uses the same -1,1 to 1,-1 co-ordinate
     * system.  So relative co-ordinates (percentage) is a simple but effective
     * way to translate from screen to world.
     *
     * This is handy for us, because want to do "future" projections for
     * calculating zooms centered on a point etc. The normal three.js
     * camera doesn't let you do that.  You could make a temporary camera etc.
     * but that's all overhead. :(
     *
     */

    return {
      wx: (relative ? absolute.x : 0) + (viewBound.getSize(new Vector2()).x / 2) * percent.px,
      wy: (relative ? absolute.y : 0) + (viewBound.getSize(new Vector2()).y / 2) * percent.py
    };
  }


  /**
   * zoom in on this position of the screen (mouse space)
   *
   * @param sx     the horizontal mouse position
   * @param sy     the vertical mouse position
   * @param deltaZ how much to move the camera up or down
   * @param noStep do not apply the stepping option, just use the deltaZ as is.
   *
   * @private
   */
  public zoomOnPoint(sx: number, sy: number, deltaZ: number, noStep: boolean = false) {

    let pz: PanZoom = this;

    //Logger.debug(`[PanZoom] zoomOnPoint - w=${pz.renderer.domElement.width} h=${pz.renderer.domElement.height} sx=${sx}, sy=${sy}, delta=${deltaZ}`);

    /* start with the current camera position */

    let  newZ: number = pz.camera.position.z;

    /* calculate the future camera height (zoom level) we want */

    if (noStep) {

      newZ += deltaZ;

    } else {

      newZ += deltaZ * pz.options.zoomStep;
    }

    if (newZ < pz.options.maxZoom) {

      /* bump stop */

      return ;

    } else if (newZ > pz.options.minZoom) {

      /* bump stop */

      return ;
    }

    /* what is the absolute 3D world position where we are now? (we want to stay "centered" here) */

    const { wx: currentX, wy: currentY } = pz.screenToWorld(sx, sy);

    /* what is the absolute 3D world position of this screen position after the given zoom? */

    const { wx: futureX, wy: futureY } =  pz.screenToWorld(sx, sy, newZ);

    /*
     * now we know the offset to use to bring the camera back over the point we are
     * "centered" on, after the zoom (camera height change)
     *
     */

    pz.camera.translateX(currentX - futureX);
    pz.camera.translateY(currentY - futureY);

    /* emit the zoom start event */

    pz.emit(
      pz.eventBuilder
        .setType(ViewerEventType.ZOOM_START)
        .setData({
          x: currentX,
          y: currentY,
          z: pz.camera.position.z
        })
        .build()
    );

    /* now just bump the camera's height ... */

    pz.camera.position.setZ(newZ);

    pz.updateProjection();

    pz.zoomer?.update();

    /* emit the zoom end event */

    pz.emit(
      pz.eventBuilder
        .setType(ViewerEventType.ZOOM_END)
        .setData({
          x: futureX,
          y: futureY,
          z: newZ
        })
        .build()
    );
  }

  /** helper to ensure the Three.js actual camera has its project matrix up to date */
  private updateProjection(): void {

    let pz: PanZoom = this;

    pz.camera?.updateProjectionMatrix();
    pz.camera?.updateMatrix();
    pz.camera?.updateMatrixWorld(true);
  }

  /**
   * for the given 3D world bounding box (x/y plane), what is the z value
   * for the camera that will just encompass viewing that bounding box?
   *
   * @param bound
   */
  public zForBox(bound: Box2): number {

    let pz: PanZoom = this;

    //Logger.debug(`[PanZoom] zoomToBox - zooming to:`, bound);

    const b: Vector2 = bound.getSize(new Vector2());

    //Logger.debug(`[PanZoom] zoomToBox -  w=${b.x}, h=${b.y}`);

    /*
     * try to scale it so that landscape kind of objects are big enough to fill as
     * much vertical as they can.
     *
     * We use method similar to:
     *
     *   https://wejn.org/2020/12/cracking-the-threejs-object-fitting-nut/
     *
     */

    const vFOV: number        = pz.camera.fov * (Math.PI / 180);
    const hFOV: number        = 2 * Math.atan (Math.tan (vFOV / 2) * pz.camera.aspect);

    const vDist: number       = b.y / (2 * Math.tan(vFOV / 2));
    const hDist: number       = b.x / (2 * Math.tan(hFOV / 2));

    return vDist >= hDist ? vDist : hDist;
  }

  /**
   * pan and zoom to this bounding box
   *
   * @param bound the bounding box to auto-fit the camera to. The bound
   * is the 3D world bound for a rectangle on the x/y plane where we draw
   * our graph of lines.  There is no depth involved.  It's a 2D graph.
   *
   */
  public zoomToBox(bound: Box2): void {

    let pz: PanZoom     = this;

    let cameraZ: number = pz.zForBox(bound);
    const c: Vector2    = bound.getCenter(new Vector2());

    //Logger.debug(`[PanZoom] zoomToBox - camera set: x=${c.x}, y=${c.y}, z=${cameraZ}`);

    /*
     * set the new camera position such that its viewing frustum just
     * includes the bounding box wanted.
     *
     */

    /* emit the zoom end event */

    pz.emit(
      pz.eventBuilder
        .setType(ViewerEventType.ZOOM_START)
        .setData({
          x: pz.camera.position.x,
          y: pz.camera.position.y,
          z: pz.camera.position.z
        })
        .build()
    );

    pz.camera.position.set(c.x, c.y, cameraZ);

    pz.updateProjection();

    /* emit the zoom end event */

    pz.emit(
      pz.eventBuilder
        .setType(ViewerEventType.ZOOM_END)
        .setData({
          x: c.x,
          y: c.y,
          z: cameraZ
        })
        .build()
    );
  }

  /**
   * register an event handler.
   *
   * @param name the unique name of the event (or an EventType value), or '*'
   * for wildcarding (match any). You can also use 'any'.
   *
   * @param callback the callback to fire on a match to an emitted event.
   *
   * @private
   */
  public onEvent(name: string|ViewerEventType, callback: EventHandler): void {

    let pz: PanZoom = this;

    if (pz.handlers.has(name)) {

      // @ts-ignore
      pz.basicEvents.get(tag).push(callback);

      return ;

    }

    /* first listener of this type */

    pz.handlers.set(name, [callback]);
  }

  /**
   * emit an event, it will be sent to handlers by event type, or any
   * handler that registered for '*' or 'any'.
   *
   * @param event the event being emitted.
   *
   * @private
   */
  private emit(event: ViewerEvent): void {

    let pz: PanZoom = this;

    /* look for a handler we can fire... */

    [event.type, '*', 'any'].forEach((tag: string): void => {

      if (!pz.handlers.has(tag)) {

        /* no listeners, nothing to do */

        return ;
      }

      // @ts-ignore
      let listeners: Eventhandler[] = pz.handlers.get(tag);

      listeners.forEach((callBack: EventHandler): void => {

        callBack(event);

      });

    });

  }
}
